import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  CircularProgress,
  Typography,
  Link,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ReactPixel from "react-facebook-pixel";
import api from "../../services/api";
import { useSnackbarAlert } from "../../hooks/useSnackbarAlert";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";
import { auth } from "../../firebase"; // ✅ centralized import
import {
  isValidName,
  isValidEmail,
  isValidPhone,
} from "../../utils/validation";

import SignupHeader from "./SignupHeader";
import RequiredFieldsForm from "./RequiredFieldsForm";
import OptionalFieldsForm from "./OptionalFieldsForm";
import TermsCheckbox from "./TermsCheckbox";
import { getUTCDateFromParts } from "../../utils/dates/getUTCDateFromParts";

const SignupFormWrapper = () => {
  const snackbar = useSnackbarAlert();
  const navigate = useNavigate();
  const [initiatedSignup, setInitiatedSignup] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phone: "",
    gender: "",
    customGender: "",
    dob: { day: "", month: "", year: "" },
    agreedToTerms: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    ReactPixel.track("ViewContent", {
      content_name: "Signup Page",
      content_category: "Signup",
    });
  }, []);

  useEffect(() => {
    if (!initiatedSignup && formData.firstName.trim().length > 1) {
      ReactPixel.track("InitiateCheckout", {
        content_name: "Signup Start",
        status: "Started Filling Form",
      });
      setInitiatedSignup(true);
    }
  }, [formData.firstName, initiatedSignup]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDobChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      dob: { ...prev.dob, [field]: value },
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!isValidName(formData.firstName)) {
      newErrors.firstName = "Please enter a valid first name";
    }

    if (!isValidName(formData.lastName)) {
      newErrors.lastName = "Please enter a valid last name";
    }

    if (!isValidEmail(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.password || formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (formData.phone && !isValidPhone(formData.phone)) {
      newErrors.phone = "Phone number must be 10 digits";
    }

    if (formData.gender === "Other" && !formData.customGender.trim()) {
      newErrors.customGender = "Please specify your gender";
    }

    if (!formData.agreedToTerms) {
      newErrors.agreedToTerms = "You must agree to proceed";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      snackbar.warning("Please fix the errors and try again.");
      return;
    }

    setLoading(true);

    try {
      // Step 1: Create Firebase account
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      const user = userCredential.user;

      // Step 2: Construct DOB in UTC if available
      const dobUTC =
        formData.dob.day && formData.dob.month && formData.dob.year
          ? getUTCDateFromParts({
              year: parseInt(formData.dob.year),
              month: parseInt(formData.dob.month),
              day: parseInt(formData.dob.day),
            })
          : undefined;

      // Step 3: Construct backend payload
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone || undefined,
        gender:
          formData.gender === "Other"
            ? formData.customGender.toLowerCase()
            : formData.gender?.toLowerCase() || undefined,
        dob: dobUTC,
        termsAccepted: formData.agreedToTerms,
        uid: user.uid,
      };

      // Step 4: Submit to backend
      const response = await api.post("/users", payload);

      // Step 5: Trigger Firebase email verification
      await sendEmailVerification(user);

      // Step 6: Show success message and redirect
      snackbar.success(
        response.data.message || "Account created. Please verify your email."
      );
      setTimeout(() => {
        navigate(
          `/signup/confirm-email?email=${encodeURIComponent(formData.email)}`
        );
      }, 2500);

      ReactPixel.track("CompleteRegistration", {
        content_name: "Signup",
        status: "Success",
        email: formData.email, // optional
      });
    } catch (error) {
      console.error("Signup error:", error);

      let message = "Signup failed. Please try again or contact support.";

      // Firebase errors
      if (error.code === "auth/email-already-in-use") {
        message = "An account with this email already exists.";
      } else if (error.code === "auth/invalid-email") {
        message = "Please enter a valid email address.";
      } else if (error.code === "auth/weak-password") {
        message = "Password should be at least 6 characters.";
      }

      // Backend errors (override if present)
      if (error.response?.data?.message) {
        message = error.response.data.message;
      } else if (error.response?.data?.error) {
        message = error.response.data.error;
      }

      snackbar.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <SignupHeader />
      <Grid container spacing={2}>
        <RequiredFieldsForm
          formData={formData}
          onChange={handleChange}
          errors={errors}
        />

        <OptionalFieldsForm
          formData={formData}
          onChange={handleChange}
          onDobChange={handleDobChange}
          errors={errors}
        />

        <TermsCheckbox
          agreed={formData.agreedToTerms}
          onChange={(val) => handleChange("agreedToTerms", val)}
          error={errors.agreedToTerms}
        />
      </Grid>

      <Button
        type="submit"
        variant="contained"
        fullWidth
        sx={{ mt: 4, py: 1.25 }}
        disabled={loading}>
        {loading ? (
          <>
            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            Creating Account...
          </>
        ) : (
          "Create Account"
        )}
      </Button>

      <Box sx={{ mt: 3, textAlign: "center" }}>
        <Typography variant="body2">
          Already a member?{" "}
          <Link href="/login" underline="hover">
            Log in
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default SignupFormWrapper;
